import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  Stack,
  SvgIcon,
  TextField,
  Typography, InputAdornment, InputLabel, OutlinedInput, Radio, RadioGroup, Accordion, AccordionDetails, AccordionSummary, Dialog, DialogContent, useMediaQuery,
  Drawer,
} from '@mui/material'
import React, { useEffect, useState } from 'react';
import { useForm, register, watch, Controller } from 'react-hook-form';
import TypeAhead from 'components/TypeAhead';
import { endpoints } from 'utils/axios';
import FormProvider from 'hooks/form/form-provider';
import { updateCandidateEducation } from 'api/users';
import { updateCandidateWorkExperience } from 'api/users';
import { useSnackbar } from 'components/snackbar';
import { updateSkills } from 'api/users';
import StarIcon from '@mui/icons-material/Star';
import DeleteSkillDialog from './DeleteSkillDialog';
import { styled, useTheme } from '@mui/material/styles';
import { is } from 'date-fns/locale';


const BpIcon = styled('span')(({ theme }) => ({
  borderRadius: 6,
  width: 20,
  height: 20,
  boxShadow: 'inset 0 0 0 1.5px rgba(16,22,26,.2), inset 0 -1px 0 rgba(16,22,26,.1)',
  backgroundImage: 'linear-gradient(180deg,hsla(0,0%,100%,.8),hsla(0,0%,100%,0))',
  '.Mui-focusVisible &': {
    outline: '2px auto rgba(19,124,189,.6)',
    outlineOffset: 2,
  },
  'input:hover ~ &': {
    backgroundColor: '#ebf1f5',
    ...theme.applyStyles('dark', {
      backgroundColor: '#30404d',
    }),
  },
  'input:disabled ~ &': {
    boxShadow: 'none',
    background: 'rgba(206,217,224,.5)',
    ...theme.applyStyles('dark', {
      background: 'rgba(57,75,89,.5)',
    }),
  },
  ...theme.applyStyles('dark', {
    boxShadow: '0 0 0 1px rgb(16 22 26 / 40%)',
    backgroundColor: '#394b59',
    backgroundImage: 'linear-gradient(180deg,hsla(0,0%,100%,.05),hsla(0,0%,100%,0))',
  }),
}));


const BpCheckedIcon = styled(BpIcon)({
  backgroundColor: '#137cbd',
  backgroundImage: 'linear-gradient(180deg,hsla(0,0%,100%,.1),hsla(0,0%,100%,0))',
  '&::before': {
    display: 'block',
    width: 20,
    height: 20,
    backgroundImage:
      "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath" +
      " fill-rule='evenodd' clip-rule='evenodd' d='M12 5c-.28 0-.53.11-.71.29L7 9.59l-2.29-2.3a1.003 " +
      "1.003 0 00-1.42 1.42l3 3c.18.18.43.29.71.29s.53-.11.71-.29l5-5A1.003 1.003 0 0012 5z' fill='%23fff'/%3E%3C/svg%3E\")",
    content: '""',
  },
  'input:hover ~ &': {
    backgroundColor: '#106ba3',
  },
});

/**
 * A custom checkbox component with a star icon as the checked state.
 * @param {object} data - The current data item.
 * @param {string} selectedId - The ID of the selected item.
 * @param {string} skillValue - The skill value of the current item.
 * @param {function} handleChange - The function to be called on change.
 * @param {string} ariaLabel - The label for the checkbox element. Default is "Custom checkbox".
 * @returns {React.ReactElement} A Checkbox element with a custom style.
 */
function BpCheckboxUniversal({
  data,
  selectedId,
  skillValue,
  handleChange,
  ariaLabel = 'Custom checkbox',
}) {
  return (
    <Checkbox
      sx={{ '&:hover': { bgcolor: 'transparent' } }}
      disableRipple
      color="default"
      checkedIcon={<BpCheckedIcon />}
      icon={<BpIcon />}
      inputProps={{ 'aria-label': ariaLabel }}
      onClick={(e) => e.stopPropagation()}
      onChange={handleChange(data)}
      checked={selectedId?.includes(data?._id)}
      disabled={(data?.skills?.length + skillValue?.length) > 8}
    />
  );
}



/**
 * UpdateReleventSkillsDrawer is a React component that provides a UI for updating relevant skills
 * in a job application context. It allows users to select, add, or remove skills associated with
 * their work experience, education, and certifications.
 *
 * @param {Function} onClose - Callback function to close the drawer.
 * @param {Array} workExperience - List of work experiences with skills.
 * @param {Array} education - List of educational qualifications with skills.
 * @param {Array} certificate - List of certifications with skills.
 * @param {Function} skillsUpdate - Function to update skills.
 * @param {Array} nonMatchingSkills - List of skills not matching the job requirements.
 * @param {Array} matchingSkills - List of skills matching the job requirements.
 * @param {Function} setNonMatchingSkills - Function to set non-matching skills.
 */

const UpdateReleventSkillsDrawer = ({
  onClose,
  workExperience,
  education,
  certificate,
  skillsUpdate,
  nonMatchingSkills,
  matchingSkills,
  setNonMatchingSkills,
}) => {
  const [matchedSkills, setMatchedChips] = useState(matchingSkills || []);
  const [selectedChips, setSelectedChips] = useState([]);

  const sortedExperience = [...workExperience].sort((a, b) => {
    const dateA = a.work_history_to ? new Date(a.work_history_to) : Infinity;
    const dateB = b.work_history_to ? new Date(b.work_history_to) : Infinity;
    return dateB - dateA;
  })
  const [workExperienceLocal, setWorkExperienceLocal] = useState(sortedExperience || []);

  const sortedEducation = [...education].sort((a, b) => {
    const dateA = a.education_to ? new Date(a.education_to) : Infinity;
    const dateB = b.education_to ? new Date(b.education_to) : Infinity;
    return dateB - dateA;
  })
  const [educationLocal, setEducationLocal] = useState(sortedEducation || []);

  const sortedCertificate = [...certificate].sort((a, b) => {
    const dateA = a.education_to ? new Date(a.education_to) : Infinity;
    const dateB = b.education_to ? new Date(b.education_to) : Infinity;
    return dateB - dateA;
  })
  const [certificateLocal, setCertificateLocal] = useState(sortedCertificate || []);

  const defaultValues = {
    skills: [],
    selectedWorkExperience: [],
    selectedEducation: [],
    selectedCertificate: [],
  };
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const methods = useForm({
    defaultValues,
    mode: 'onChange',
  });

  const {
    register,
    handleSubmit,
    control,
    getValues,
    setValue,
    formState: { errors, isDirty, isValid, dirtyFields, isSubmitting },
    watch,
  } = methods;

  const { enqueueSnackbar } = useSnackbar();
  const [skillValue, setSkillValue] = useState([]);

  useEffect(() => {
    setValue('skills', skillValue);
  }, [skillValue]);

  /**
   * Handles the deletion of a skill by filtering it from the skills array of the corresponding
   * work experience, education, or certification. If the deletion would result in less than 3
   * skills, an error message is shown and the deletion is not performed. If the deletion is valid,
   * the component state is updated with the new skills array and the corresponding selected
   * items are updated with the new skills array and a flag indicating that the item was modified.
   * @param {object} data - The current data item.
   * @param {string} skill - The skill to be deleted.
   * @param {string} type - The type of the current data item (experience, education, or certificate).
   */
  const handleOpenDeleteSkillDialog = (data, skill, type) => {
    const updatedSkills = data?.skills?.filter((item) => item !== skill);
    if (updatedSkills.length < 3) {
      enqueueSnackbar("Minimum of 3 Skills should be present", { variant: 'error' });
      return;
    }

    let newSelectedArray;
    const itemWithUpdatedSkills = { ...data, skills: updatedSkills };

    if (type === "experience") {
      setWorkExperienceLocal(prev =>
        prev.map(item => item._id === data._id ? { ...item, skills: updatedSkills } : item)
      );

      const currentSelectedWorkExperience = getValues('selectedWorkExperience') || [];
      const itemIndex = currentSelectedWorkExperience.findIndex(item => item._id === data._id);

      if (itemIndex > 0) {
        newSelectedArray = currentSelectedWorkExperience.map((item, idx) =>
          idx === itemIndex ? itemWithUpdatedSkills : item
        );
      } else {
        newSelectedArray = [...currentSelectedWorkExperience, { ...itemWithUpdatedSkills, isNotChecked: true }];
      }
      setValue('selectedWorkExperience', newSelectedArray, { shouldDirty: true });

    } else if (type === "education") {
      setEducationLocal(prev =>
        prev.map(item => item._id === data._id ? { ...item, skills: updatedSkills } : item)
      );

      const currentSelectedEducation = getValues('selectedEducation') || [];
      const itemIndex = currentSelectedEducation.findIndex(item => item._id === data._id);

      if (itemIndex > 0) {
        newSelectedArray = currentSelectedEducation.map((item, idx) =>
          idx === itemIndex ? itemWithUpdatedSkills : item
        );
      } else {
        newSelectedArray = [...currentSelectedEducation, { ...itemWithUpdatedSkills, isNotChecked: true }];
      }
      setValue('selectedEducation', newSelectedArray, { shouldDirty: true });

    } else if (type === "certificate") {
      setCertificateLocal(prev =>
        prev.map(item => item._id === data._id ? { ...item, skills: updatedSkills } : item)
      );

      const currentSelectedCertificate = getValues('selectedCertificate') || [];
      const itemIndex = currentSelectedCertificate.findIndex(item => item._id === data._id);

      if (itemIndex > 0) {
        newSelectedArray = currentSelectedCertificate.map((item, idx) =>
          idx === itemIndex ? itemWithUpdatedSkills : item
        );
      } else {
        newSelectedArray = [...currentSelectedCertificate, { ...itemWithUpdatedSkills, isNotChecked: true }];
      }
      setValue('selectedCertificate', newSelectedArray, { shouldDirty: true });
    }
  };

  const [selectedId, setSelectedId] = useState([]);

  /**
   * Handles a change in the checked state of a work experience checkbox.
   * @param {Object} experienceItemFromUI - The work experience item to update.
   * @param {Event} event - The event from the checkbox change.
   * 
   * If the checkbox is being checked, adds the item to the form state with isNotChecked set to false.
   * If the checkbox is being unchecked, marks the item as not checked in the form state if it has been modified
   * (i.e., skills were deleted), otherwise removes it from the form state.
   */
  const handleWorkExperienceChange = (experienceItemFromUI) => (event) => {
    const { checked } = event.target;
    const uniqueIdentifier = experienceItemFromUI._id;
    const formValues = getValues('selectedWorkExperience') || [];

    const originalItem = workExperience.find(exp => exp._id === uniqueIdentifier);

    // Determine if skills were deleted by comparing the current item's skills (in local state) with the original.
    const hasBeenModified = originalItem && JSON.stringify(originalItem.skills) !== JSON.stringify(experienceItemFromUI.skills);

    if (checked) {
      // User is CHECKING the box. This is for adding NEW skills.
      setSelectedId(prev => [...prev, uniqueIdentifier]);
      const existingIndex = formValues.findIndex(item => item._id === uniqueIdentifier);

      if (existingIndex > -1) {
        // Item was already in the list (e.g., from a skill delete). Update it to be 'checked'.
        const updatedValues = [...formValues];
        updatedValues[existingIndex] = { ...experienceItemFromUI, isNotChecked: false };
        setValue('selectedWorkExperience', updatedValues, { shouldDirty: true });
      } else {
        // It's a fresh check, add the item.
        setValue('selectedWorkExperience', [...formValues, experienceItemFromUI], { shouldDirty: true });
      }
    } else {
      // User is UNCHECKING the box.
      setSelectedId(prev => prev.filter(id => id !== uniqueIdentifier));

      if (hasBeenModified) {
        // The item's skills were deleted. DO NOT REMOVE it from the form state.
        // Instead, mark it as not checked so we only submit the skill deletion, not add new skills.
        const existingIndex = formValues.findIndex(item => item._id === uniqueIdentifier);
        if (existingIndex > -1) {
          const updatedValues = [...formValues];
          updatedValues[existingIndex].isNotChecked = true;
          setValue('selectedWorkExperience', updatedValues, { shouldDirty: true });
        }
      } else {
        // The item was NOT modified. It's safe to remove it completely.
        setValue('selectedWorkExperience', formValues.filter(item => item._id !== uniqueIdentifier), { shouldDirty: true });
      }
    }
  };

  /**
   * Handles a checkbox change event for an education item.
   * If the checkbox is checked, adds the item to the list of selected education.
   * If the checkbox is unchecked, removes the item from the list of selected education.
   * If the item was modified (e.g., the user deleted skills), marks it as not checked
   * so it is not submitted to the server as a new education entry.
   * @param {Object} educationItemFromUI - The education item to be added or removed.
   * @param {Object} event - The event object from the checkbox change.
   */
  const handleEducationChange = (educationItemFromUI) => (event) => {
    const { checked } = event.target;
    const uniqueIdentifier = educationItemFromUI._id;
    const formValues = getValues('selectedEducation') || [];
    const originalItem = education.find(edu => edu._id === uniqueIdentifier);
    const hasBeenModified = originalItem && JSON.stringify(originalItem.skills) !== JSON.stringify(educationItemFromUI.skills);

    if (checked) {
      setSelectedId(prev => [...prev, uniqueIdentifier]);
      const existingIndex = formValues.findIndex(item => item._id === uniqueIdentifier);
      if (existingIndex > -1) {
        const updatedValues = [...formValues];
        updatedValues[existingIndex] = { ...educationItemFromUI, isNotChecked: false };
        setValue('selectedEducation', updatedValues, { shouldDirty: true });
      } else {
        setValue('selectedEducation', [...formValues, educationItemFromUI], { shouldDirty: true });
      }
    } else {
      setSelectedId(prev => prev.filter(id => id !== uniqueIdentifier));
      if (hasBeenModified) {
        const existingIndex = formValues.findIndex(item => item._id === uniqueIdentifier);
        if (existingIndex > -1) {
          const updatedValues = [...formValues];
          updatedValues[existingIndex].isNotChecked = true;
          setValue('selectedEducation', updatedValues, { shouldDirty: true });
        }
      } else {
        setValue('selectedEducation', formValues.filter(item => item._id !== uniqueIdentifier), { shouldDirty: true });
      }
    }
  };

  /**
   * Handles a checkbox change event for a certification item in the certifications list.
   * If the checkbox is checked, adds the item to the list of selected certifications.
   * If the checkbox is unchecked, removes the item from the list of selected certifications.
   * If the item was modified (e.g., the user deleted skills), marks it as not checked
   * so it is not submitted to the server as a new certification entry.
   * @param {Object} certificateItemFromUI - The certification item to be added or removed.
   * @param {Object} event - The event object from the checkbox change.
   */
  const handleCertificateChange = (certificateItemFromUI) => (event) => {
    const { checked } = event.target;
    const uniqueIdentifier = certificateItemFromUI._id;
    const formValues = getValues('selectedCertificate') || [];
    const originalItem = certificate.find(cert => cert._id === uniqueIdentifier);
    const hasBeenModified = originalItem && JSON.stringify(originalItem.skills) !== JSON.stringify(certificateItemFromUI.skills);

    if (checked) {
      setSelectedId(prev => [...prev, uniqueIdentifier]);
      const existingIndex = formValues.findIndex(item => item._id === uniqueIdentifier);
      if (existingIndex > -1) {
        const updatedValues = [...formValues];
        updatedValues[existingIndex] = { ...certificateItemFromUI, isNotChecked: false };
        setValue('selectedCertificate', updatedValues, { shouldDirty: true });
      } else {
        setValue('selectedCertificate', [...formValues, certificateItemFromUI], { shouldDirty: true });
      }
    } else {
      setSelectedId(prev => prev.filter(id => id !== uniqueIdentifier));
      if (hasBeenModified) {
        const existingIndex = formValues.findIndex(item => item._id === uniqueIdentifier);
        if (existingIndex > -1) {
          const updatedValues = [...formValues];
          updatedValues[existingIndex].isNotChecked = true;
          setValue('selectedCertificate', updatedValues, { shouldDirty: true });
        }
      } else {
        setValue('selectedCertificate', formValues.filter(item => item._id !== uniqueIdentifier), { shouldDirty: true });
      }
    }
  };

  const watchedWorkExperience = watch('selectedWorkExperience');
  const watchedEducation = watch('selectedEducation');
  const watchedCertificate = watch('selectedCertificate');

  // Scenario 1: Check if any skill has been deleted.
  // An item with `isNotChecked: true` represents a preserved skill deletion change.
  const hasDeletedSkills =
    !!watchedWorkExperience?.some(item => item.isNotChecked) ||
    !!watchedEducation?.some(item => item.isNotChecked) ||
    !!watchedCertificate?.some(item => item.isNotChecked);

  // Scenario 2: Check if there's a valid skill addition ready.
  // This requires new skills to be selected AND at least one item to be checked.
  const hasPendingAdditions =
    skillValue.length > 0 &&
    (
      !!watchedWorkExperience?.some(item => !item.isNotChecked) ||
      !!watchedEducation?.some(item => !item.isNotChecked) ||
      !!watchedCertificate?.some(item => !item.isNotChecked)
    );

  const isActionable = hasDeletedSkills || hasPendingAdditions;

  /**
   * Handles the selection of skills. If the label is already selected, it
   * removes it from the list of skills. If the label is not selected and the
   * length of the skills list is less than 6, it adds the label to the list.
   * If the length of the skills list is 6 or more, it does nothing.
   * @param {string} label - The label of the skill to be selected or deselected.
   */
  const handleClick = (selectedSkill) => {
    const label = selectedSkill?.skill;
    if (!label) return false;
    if (selectedChips.includes(label)) {
      setSelectedChips(selectedChips.filter((chip) => chip !== label));
      setMatchedChips(matchedSkills.filter((chip) => chip !== label));
      setSkillValue(skillValue.filter((chip) => chip !== label));
    } else {
      // if (skillValue?.length <= 5) {
      setSelectedChips([...selectedChips, label]);
      setMatchedChips([...matchedSkills, label]);
      setSkillValue([...skillValue, label]);
      validation(selectedId, [...skillValue, label], selectedId);
      // }
    }
  };
  const [openAccordions, setOpenAccordions] = useState({});
  const [disclaimerMap, setDisclaimerMap] = useState({});

  /**
   * Handles the expansion and collapse of the accordion containing the skills,
   * as well as the display of the disclaimer when the length of the skills list
   * is 3.
   * @param {string} itemId - the id of the item to be checked
   * @param {number} length - the length of the skills list
   */
  const checkSkills = (itemId, length) => {
    setOpenAccordions(prev => {
      const isNowOpen = !prev[itemId];
      setDisclaimerMap(prevDisclaimer => ({
        ...prevDisclaimer,
        [itemId]: isNowOpen && length === 3
      }));

      return {
        ...prev,
        [itemId]: isNowOpen
      };
    });
  };

  const [error, setError] = useState(false);

  /**
   * Validates the selected skills against the user's work experience, education, and certifications.
   * Updates the selected work experience, education, and certifications by removing entries that exceed 
   * the allowed skill count based on the provided skills.
   *
   * @param {Array<string>} ids - Array of IDs to check against work experience, education, and certificates.
   * @param {Array<string>} skills - Array of selected skill labels.
   * @param {Array<string>} setIds - Array of IDs to update after validation.
   * @returns {boolean} - Returns true if any of the entries are invalidated and removed, false otherwise.
   */

  const validation = (ids, skills, setIds) => {
    const selectedWorkExperience = getValues('selectedWorkExperience');
    const selectedEducation = getValues('selectedEducation');
    const selectedCertificate = getValues('selectedCertificate');

    let valid = false;

    /**
     * Checks if the total number of skills in the experience plus the provided
     * skill value is greater than 8.
     * @param {object} experience - The experience to check.
     * @param {Array<string>} skillValue - The array of selected skill labels.
     * @returns {boolean} - Returns true if the total number of skills is greater than 8, false otherwise.
     */
    const isValid = (experience, skillValue) => {
      return Boolean((skillValue?.length > (8 - (experience?.skills?.length || 0))));
    };

    let newSetId = [...setIds];
    let newWorkExperience = [...selectedWorkExperience];
    let newEducation = [...selectedEducation];
    let newCertificate = [...selectedCertificate];

    if (Array.isArray(ids)) {
      ids.forEach((id) => {
        const workExp = workExperience.find((experience) => experience?._id === id);
        if (workExp && isValid(workExp, skills)) {
          valid = true;
          setError(true);
          newWorkExperience = newWorkExperience.filter((experience) => experience._id !== workExp._id);
          newSetId = newSetId.filter((chip) => chip !== workExp._id);
        }

        const edu = education.find((experience) => experience?._id === id);
        if (edu && isValid(edu, skills)) {
          valid = true;
          setError(true);
          newEducation = newEducation.filter((experience) => experience._id !== edu._id);
          newSetId = newSetId.filter((chip) => chip !== edu._id);
        }

        const certi = certificate.find((experience) => experience?._id === id);
        if (certi && isValid(certi, skills)) {
          valid = true;
          setError(true);
          newCertificate = newCertificate.filter((experience) => experience._id !== certi._id);
          newSetId = newSetId.filter((chip) => chip !== certi._id);
        }
      });
      setValue('selectedWorkExperience', newWorkExperience);
      setValue('selectedEducation', newEducation);
      setValue('selectedCertificate', newCertificate);
      setSelectedId(newSetId);

      if (!valid) setError(false);

      return valid;
    }

    return false;
  };

  const onSubmit = handleSubmit(async (formData) => {
    try {
      const finalPayload = {
        skills: formData.skills,

        selectedWorkExperience: formData.selectedWorkExperience.map(exp => {
          if (exp.isNotChecked) {
            return {
              _id: exp._id,
              skills: exp.skills,
            };
          }

          return {
            _id: exp._id,
            skills: [...new Set([...exp.skills, ...formData.skills])]
          };
        }),

        // Process education
        selectedEducation: formData.selectedEducation.map(edu => {
          if (edu.isNotChecked) {
            return { _id: edu._id, skills: edu.skills };
          }
          return { _id: edu._id, skills: [...new Set([...edu.skills, ...formData.skills])] };
        }),

        // Process certificate
        selectedCertificate: formData.selectedCertificate.map(cert => {
          if (cert.isNotChecked) {
            return { _id: cert._id, skills: cert.skills };
          }
          return { _id: cert._id, skills: [...new Set([...cert.skills, ...formData.skills])] };
        }),
      };

      // The finalPayload now accurately represents the user's intent.
      const response = await updateSkills(finalPayload);

      if (response && response?.data && response?.success) {
        await skillsUpdate(matchedSkills);
        setSelectedChips([]);
        onClose();
        enqueueSnackbar('Skills updated successfully', { variant: 'success' });
      } else {
        enqueueSnackbar('Failed to update skills.', { variant: 'error' });
      }
    } catch (error) {
      console.error('Error to set skills. Please try again.', error);
      enqueueSnackbar('Error updating skills. Please try again.', { variant: 'error' });
    }
  });

  return (
    <Box sx={{ width: { xs: '100%', md: '450px' } }} className="job-app-drawer-container update-skills-drawer-container" role="presentation" >
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <Box className="drawer-header">
          <Stack direction="row" alignItems={'flex-start'} spacing={1}>
            <IconButton aria-label="delete" onClick={onClose} sx={{ paddingTop: 0, paddingBottom: 0 }}>
              <SvgIcon>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M6.22796 5.04477C5.90134 4.71815 5.37178 4.71815 5.04516 5.04477C4.71854 5.37139 4.71854 5.90095 5.04516 6.22757L10.8174 11.9998L5.04516 17.772C4.71854 18.0987 4.71854 18.6282 5.04516 18.9548C5.37178 19.2815 5.90134 19.2815 6.22796 18.9548L12.0002 13.1826L17.7724 18.9548C18.0991 19.2815 18.6286 19.2815 18.9552 18.9548C19.2819 18.6282 19.2819 18.0987 18.9552 17.772L13.183 11.9998L18.9552 6.22757C19.2819 5.90095 19.2819 5.37139 18.9552 5.04477C18.6286 4.71815 18.0991 4.71815 17.7724 5.04477L12.0002 10.817L6.22796 5.04477Z" fill="white" />
                </svg>
              </SvgIcon>
            </IconButton>

            <Box>
              <Typography className="title">Skill Update</Typography>
            </Box>
          </Stack>
        </Box>

        <Box className="drawer-content update-skills-content">
          <Box className="skills-section select-relevent-skills-v2">
            <Stack direction="row" flexWrap="wrap">
              <Typography sx={{ paddingBottom: '12px', fontSize: '14px', lineHeight: '20px' }} variant="body2">Following is the list of company preferred skills missing from your profile. Please update as applicable.</Typography>
              <Box>
                {nonMatchingSkills?.map((label, index) => (
                  <Chip
                    label={
                      label?.is_core ? <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                        <span>{label?.skill}</span>
                        <StarIcon className="star-icon" sx={{ color: '#faaf00', fontSize: '12px', marginLeft: '5px' }} />
                      </Box> : label?.skill
                    } key={`skills_${index}`}
                    onClick={() => handleClick(label)} className={skillValue?.includes(label?.skill) ? "selected-skill" : ''} variant="outlined" />
                ))}
              </Box>
            </Stack>
          </Box>

          {educationLocal?.length > 0 && (
            <Box className="skills-section" mb={2}>
              <Stack direction="row" justifyContent={'space-between'}>
                <Typography className="title">Education</Typography>
                <Typography className="title">Skills</Typography>
              </Stack>
              <Box>
                <Stack>
                  {educationLocal?.map((data, index) => (<>
                    <Stack>
                      <Accordion>
                        <AccordionSummary
                          aria-controls="panel1-content"
                          id="panel1-header"
                          sx={{
                            padding: 0,
                            margin: 0,
                          }}
                        >
                          <Stack direction="row" justifyContent={'space-between'} sx={{ width: '100%' }}>
                            <Stack>
                              <FormControlLabel
                                key={`edu_skills_${index}`}
                                onClick={(e) => e.stopPropagation()}
                                control={
                                  <BpCheckboxUniversal
                                    data={data}
                                    selectedId={selectedId}
                                    skillValue={skillValue}
                                    handleChange={handleEducationChange}
                                    ariaLabel="Education checkbox"
                                  />

                                }
                                label={`${data?.degree_received || ''} at ${data?.education_institute_name || ''}`}
                              />
                              {data?.skills?.length >= 8 ? (
                                <Typography
                                  variant="caption"
                                  display="block"
                                  gutterBottom
                                  className="error-text"
                                  size="small"
                                  component="p"
                                  style={{ color: 'red' }}
                                  marginLeft={'30px'}
                                >
                                  Max limit reached, remove some existing skills to add new.
                                </Typography>
                              ) : (
                                <Typography
                                  variant="caption"
                                  display="block"
                                  gutterBottom
                                  className="error-text"
                                  size="small"
                                  component="p"
                                  style={{
                                    color: disclaimerMap[data?._id]
                                      ? 'red'
                                      : error || (skillValue?.length > (8 - (data?.skills?.length || 0)))
                                        ? '#eb810d'
                                        : 'inherit'
                                  }}
                                  marginLeft={'30px'}
                                >
                                  {disclaimerMap[data?._id]
                                    ? "Minimum 3 skills should be present"
                                    : skillValue?.length > (8 - (data?.skills?.length || 0))
                                      ? `Only ${8 - (data?.skills?.length || 0)} new skill may be added to this experience`
                                      : null}
                                </Typography>
                              )}
                            </Stack>
                            <Stack direction="row" sx={{ gap: '8px' }} onClick={() => { checkSkills(data?._id, data?.skills?.length) }}>
                              <Typography className="skill-count-txt" component="span" style={{ color: data?.skills?.length >= 8 ? 'red' : 'inherit' }}>{`${data?.skills?.length}/8`}</Typography>
                              <IconButton className="accordion-arrow" aria-label="delete" disableRipple >
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                  <path d="M3.4001 7.42578L8.83343 12.8591C9.4751 13.5008 10.5251 13.5008 11.1668 12.8591L16.6001 7.42578" stroke="#1575C6" stroke-width="1.3" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" />
                                </svg>
                              </IconButton>
                              <Typography sx={{ display: 'none' }} className="view-hide-skill-txt" component="span">View Skills</Typography>
                            </Stack>
                          </Stack>
                        </AccordionSummary>
                        <AccordionDetails>
                          {data?.skills?.map((skill, skillIndex) => (
                            <Stack spacing={1.5} sx={{ marginTop: '10px', marginLeft: '30px' }}>
                              <Stack direction="row" alignItems={'center'} spacing={1.5}>
                                <Typography className="skill-name" component="span">{skill}</Typography>
                                <IconButton className="delete-skill-icon" aria-label="delete" onClick={(event) => {
                                  event.stopPropagation();
                                  handleOpenDeleteSkillDialog(data, skill, "education")
                                }}>
                                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                    <path fill-rule="evenodd" clip-rule="evenodd" d="M11.0948 2.37734C11.0948 2.22733 11.0353 2.08301 10.9289 1.97715C10.8231 1.87074 10.6787 1.81131 10.5287 1.81131H8.70671L8.44689 0.771503C8.33369 0.317545 7.92557 -0.000564375 7.45803 7.54076e-07H4.54298C4.07543 -0.000565244 3.66732 0.317545 3.55412 0.771503L3.29431 1.81131H1.47228C1.15983 1.81131 0.90625 2.06489 0.90625 2.37734C0.90625 2.68978 1.15983 2.94337 1.47228 2.94337H1.50171C1.46096 3.10186 1.44737 3.26658 1.46096 3.42959L2.14473 10.2668C2.24096 11.2523 3.0702 12.0027 4.06023 12H7.94092C8.93091 12.0028 9.7602 11.2523 9.85642 10.2668L10.5402 3.42901C10.5538 3.266 10.5396 3.10185 10.4994 2.94335H10.5289C10.6789 2.94335 10.8232 2.88392 10.9291 2.77751C11.0355 2.67166 11.0948 2.52733 11.0948 2.37734ZM4.63071 1.13206H7.3697L7.53951 1.8113H4.46087L4.63071 1.13206ZM9.32707 3.05494C9.3916 3.1257 9.4233 3.22135 9.41311 3.31702L8.72933 10.1542C8.69028 10.5601 8.34839 10.8691 7.94085 10.868H4.06015C3.65261 10.8691 3.31072 10.5601 3.27167 10.1542L2.58789 3.3176C2.57827 3.22194 2.6094 3.12684 2.67393 3.05552C2.73846 2.9842 2.82959 2.94345 2.92582 2.94345H9.07529C9.17151 2.94288 9.26255 2.98362 9.32707 3.05494ZM5.09484 4.52832C4.94483 4.52832 4.80051 4.58775 4.69465 4.69416C4.58824 4.80001 4.52881 4.94435 4.52881 5.09434V7.81131C4.52881 8.12375 4.78239 8.37734 5.09484 8.37734C5.40728 8.37734 5.66086 8.12375 5.66086 7.81131V5.09434C5.66086 4.94434 5.60143 4.80001 5.49502 4.69416C5.38917 4.58774 5.24483 4.52832 5.09484 4.52832ZM6.50599 4.69416C6.61185 4.58775 6.75617 4.52832 6.90618 4.52832C7.05617 4.52832 7.20051 4.58774 7.30636 4.69416C7.41277 4.80001 7.4722 4.94434 7.4722 5.09434V7.81131C7.4722 8.12375 7.21862 8.37734 6.90618 8.37734C6.59373 8.37734 6.34015 8.12375 6.34015 7.81131V5.09434C6.34015 4.94435 6.39958 4.80001 6.50599 4.69416Z" fill="#FF4949" />
                                  </svg>
                                </IconButton>
                              </Stack>
                            </Stack>
                          ))}
                        </AccordionDetails>
                      </Accordion>
                    </Stack>
                    <Divider sx={{ borderStyle: 'dashed', borderWidth: '1px', marginTop: '1rem', marginBottom: '1rem' }} />
                  </>))}
                </Stack>
              </Box>
            </Box>
          )}

          {workExperienceLocal?.length > 0 && (
            <Box className="skills-section" mb={2}>

              <Stack direction="row" justifyContent={'space-between'}>
                <Typography className="title">Experience</Typography>
                <Typography className="title">Skills</Typography>
              </Stack>
              <Box>
                {workExperienceLocal?.map(
                  (experience, index) =>
                    experience.organization_name !== 'Work Gap' && (<>
                      <Stack>
                        <Accordion>
                          <AccordionSummary
                            aria-controls="panel1-content"
                            id="panel1-header"
                            sx={{
                              padding: 0,
                              margin: 0,
                            }}
                            onClick={(e) => {
                              if (e.target.closest('.MuiFormControlLabel-root')) {
                                e.stopPropagation();
                              }
                            }}
                          >
                            <Stack direction="row" justifyContent={'space-between'} sx={{ width: '100%' }}>
                              <Stack>
                                <FormControlLabel
                                  key={`work_skills_${index}`}
                                  onClick={(e) => e.stopPropagation()}
                                  control={
                                    <BpCheckboxUniversal
                                      data={experience}
                                      selectedId={selectedId}
                                      skillValue={skillValue}
                                      handleChange={handleWorkExperienceChange}
                                      ariaLabel="Work experience checkbox"
                                    />

                                  }
                                  label={`${experience?.highest_designation || ''} at ${experience?.organization_name || ''}`}
                                />
                                {experience?.skills?.length >= 8 ? (
                                  <Typography
                                    variant="caption"
                                    display="block"
                                    gutterBottom
                                    className="error-text"
                                    size="small"
                                    component="p"
                                    style={{ color: 'red' }}
                                    marginLeft={'30px'}
                                  >
                                    Max limit reached, remove some existing skills to add new.
                                  </Typography>
                                ) : (
                                  <Typography
                                    component="span"
                                    variant="caption"
                                    display="block"
                                    gutterBottom
                                    className="error-text"
                                    size="small"
                                    style={{
                                      color: disclaimerMap[experience?._id]
                                        ? 'red'
                                        : error || (skillValue?.length > (8 - (experience?.skills?.length || 0)))
                                          ? '#eb810d'
                                          : 'inherit'
                                    }}
                                    marginLeft={'30px'}
                                  >
                                    {disclaimerMap[experience?._id]
                                      ? "Minimum 3 skills should be present"
                                      : (skillValue?.length > (8 - (experience?.skills?.length || 0)))
                                        ? `Only ${8 - (experience?.skills?.length || 0)} new skill may be added to this experience`
                                        : null}

                                  </Typography>
                                )
                                }
                              </Stack>

                              <Stack direction="row" sx={{ gap: '8px' }} onClick={() => { checkSkills(experience?._id, experience?.skills?.length) }}>
                                <Typography className="skill-count-txt" component="span" style={{ color: experience?.skills?.length >= 8 ? 'red' : 'inherit' }}>{`${experience?.skills?.length}/8`}</Typography>
                                <IconButton className="accordion-arrow" aria-label="delete" disableRipple >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M3.4001 7.42578L8.83343 12.8591C9.4751 13.5008 10.5251 13.5008 11.1668 12.8591L16.6001 7.42578" stroke="#1575C6" stroke-width="1.3" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" />
                                  </svg>
                                </IconButton>
                                <Typography sx={{ display: 'none' }} className="view-hide-skill-txt" component="span">View Skills</Typography>
                              </Stack>

                            </Stack>
                          </AccordionSummary>
                          <AccordionDetails>
                            {experience?.skills?.map((skill, skillIndex) => (
                              <Stack spacing={1.5} sx={{ marginTop: '10px', marginLeft: '30px' }}>
                                <Stack direction="row" alignItems={'center'} spacing={1.5}>
                                  <Typography className="skill-name" component="span">{skill}</Typography>
                                  <IconButton className="delete-skill-icon" aria-label="delete" onClick={(event) => {
                                    event.stopPropagation();
                                    handleOpenDeleteSkillDialog(experience, skill, "experience")
                                  }}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                      <path fill-rule="evenodd" clip-rule="evenodd" d="M11.0948 2.37734C11.0948 2.22733 11.0353 2.08301 10.9289 1.97715C10.8231 1.87074 10.6787 1.81131 10.5287 1.81131H8.70671L8.44689 0.771503C8.33369 0.317545 7.92557 -0.000564375 7.45803 7.54076e-07H4.54298C4.07543 -0.000565244 3.66732 0.317545 3.55412 0.771503L3.29431 1.81131H1.47228C1.15983 1.81131 0.90625 2.06489 0.90625 2.37734C0.90625 2.68978 1.15983 2.94337 1.47228 2.94337H1.50171C1.46096 3.10186 1.44737 3.26658 1.46096 3.42959L2.14473 10.2668C2.24096 11.2523 3.0702 12.0027 4.06023 12H7.94092C8.93091 12.0028 9.7602 11.2523 9.85642 10.2668L10.5402 3.42901C10.5538 3.266 10.5396 3.10185 10.4994 2.94335H10.5289C10.6789 2.94335 10.8232 2.88392 10.9291 2.77751C11.0355 2.67166 11.0948 2.52733 11.0948 2.37734ZM4.63071 1.13206H7.3697L7.53951 1.8113H4.46087L4.63071 1.13206ZM9.32707 3.05494C9.3916 3.1257 9.4233 3.22135 9.41311 3.31702L8.72933 10.1542C8.69028 10.5601 8.34839 10.8691 7.94085 10.868H4.06015C3.65261 10.8691 3.31072 10.5601 3.27167 10.1542L2.58789 3.3176C2.57827 3.22194 2.6094 3.12684 2.67393 3.05552C2.73846 2.9842 2.82959 2.94345 2.92582 2.94345H9.07529C9.17151 2.94288 9.26255 2.98362 9.32707 3.05494ZM5.09484 4.52832C4.94483 4.52832 4.80051 4.58775 4.69465 4.69416C4.58824 4.80001 4.52881 4.94435 4.52881 5.09434V7.81131C4.52881 8.12375 4.78239 8.37734 5.09484 8.37734C5.40728 8.37734 5.66086 8.12375 5.66086 7.81131V5.09434C5.66086 4.94434 5.60143 4.80001 5.49502 4.69416C5.38917 4.58774 5.24483 4.52832 5.09484 4.52832ZM6.50599 4.69416C6.61185 4.58775 6.75617 4.52832 6.90618 4.52832C7.05617 4.52832 7.20051 4.58774 7.30636 4.69416C7.41277 4.80001 7.4722 4.94434 7.4722 5.09434V7.81131C7.4722 8.12375 7.21862 8.37734 6.90618 8.37734C6.59373 8.37734 6.34015 8.12375 6.34015 7.81131V5.09434C6.34015 4.94435 6.39958 4.80001 6.50599 4.69416Z" fill="#FF4949" />
                                    </svg>
                                  </IconButton>
                                </Stack>
                              </Stack>
                            ))}
                          </AccordionDetails>

                        </Accordion>
                      </Stack>
                      <Divider sx={{ borderStyle: 'dashed', borderWidth: '1px', marginTop: '1rem', marginBottom: '1rem' }} />
                    </>))}
              </Box>
            </Box>
          )}

          {certificateLocal?.length > 0 && (
            <Box className="skills-section" >

              <Stack direction="row" justifyContent={'space-between'}>
                <Typography className="title">Certification</Typography>
                <Typography className="title">Skills</Typography>
              </Stack>
              <Box>
                <Stack>
                  {certificateLocal?.map((data, index) => (<>
                    <Stack>
                      <Accordion>
                        <AccordionSummary
                          aria-controls="panel1-content"
                          id="panel1-header"
                          sx={{
                            padding: 0,
                            margin: 0,
                          }}
                        >
                          <Stack direction="row" justifyContent={'space-between'} sx={{ width: '100%' }}>
                            <Stack>
                              <FormControlLabel
                                key={`cert_skills_${index}`}
                                onClick={(e) => e.stopPropagation()}
                                control={
                                  <BpCheckboxUniversal
                                    data={data}
                                    selectedId={selectedId}
                                    skillValue={skillValue}
                                    handleChange={handleCertificateChange}
                                    ariaLabel="Certificate checkbox"
                                  />

                                }
                                label={`${data?.certificate_received || ''} at ${data?.education_institute_name || ''}`}
                              />
                              {data?.skills?.length >= 8 ? (
                                <Typography
                                  variant="caption"
                                  display="block"
                                  gutterBottom
                                  className="error-text"
                                  size="small"
                                  component="p"
                                  style={{ color: 'red' }}
                                  marginLeft={'30px'}
                                >
                                  Max limit reached, remove some existing skills to add new.
                                </Typography>
                              ) : (
                                <Typography
                                  variant="caption"
                                  display="block"
                                  gutterBottom
                                  className="error-text"
                                  size="small"
                                  component="p"
                                  style={{
                                    color: disclaimerMap[data?._id]
                                      ? 'red'
                                      : error || (skillValue?.length > (8 - (data?.skills?.length || 0)))
                                        ? '#eb810d'
                                        : 'inherit'
                                  }}
                                  marginLeft={'30px'}
                                >
                                  {disclaimerMap[data?._id]
                                    ? "Minimum 3 skills should be present"
                                    : skillValue?.length > (8 - (data?.skills?.length || 0))
                                      ? `Only ${8 - (data?.skills?.length || 0)} new skill may be added to this experience`
                                      : null}

                                </Typography>
                              )}
                            </Stack>
                            <Stack direction="row" sx={{ gap: '8px' }} onClick={() => { checkSkills(data?._id, data?.skills?.length) }}>
                              <Typography className="skill-count-txt" component="span" style={{ color: data?.skills?.length >= 8 ? 'red' : 'inherit' }}>{`${data?.skills?.length}/8`}</Typography>
                              <IconButton className="accordion-arrow" aria-label="delete" disableRipple >
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                  <path d="M3.4001 7.42578L8.83343 12.8591C9.4751 13.5008 10.5251 13.5008 11.1668 12.8591L16.6001 7.42578" stroke="#1575C6" stroke-width="1.3" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" />
                                </svg>
                              </IconButton>
                              <Typography sx={{ display: 'none' }} className="view-hide-skill-txt" component="span">View Skills</Typography>
                            </Stack>
                          </Stack>
                        </AccordionSummary>
                        <AccordionDetails>
                          {data?.skills?.map((skill, skillIndex) => (
                            <Stack spacing={1.5} sx={{ marginTop: '10px', marginLeft: '30px' }}>
                              <Stack direction="row" alignItems={'center'} spacing={1.5}>
                                <Typography className="skill-name" component="span">{skill}</Typography>
                                <IconButton className="delete-skill-icon" aria-label="delete" onClick={(event) => {
                                  event.stopPropagation();
                                  handleOpenDeleteSkillDialog(data, skill, "certificate")
                                }}>
                                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                    <path fill-rule="evenodd" clip-rule="evenodd" d="M11.0948 2.37734C11.0948 2.22733 11.0353 2.08301 10.9289 1.97715C10.8231 1.87074 10.6787 1.81131 10.5287 1.81131H8.70671L8.44689 0.771503C8.33369 0.317545 7.92557 -0.000564375 7.45803 7.54076e-07H4.54298C4.07543 -0.000565244 3.66732 0.317545 3.55412 0.771503L3.29431 1.81131H1.47228C1.15983 1.81131 0.90625 2.06489 0.90625 2.37734C0.90625 2.68978 1.15983 2.94337 1.47228 2.94337H1.50171C1.46096 3.10186 1.44737 3.26658 1.46096 3.42959L2.14473 10.2668C2.24096 11.2523 3.0702 12.0027 4.06023 12H7.94092C8.93091 12.0028 9.7602 11.2523 9.85642 10.2668L10.5402 3.42901C10.5538 3.266 10.5396 3.10185 10.4994 2.94335H10.5289C10.6789 2.94335 10.8232 2.88392 10.9291 2.77751C11.0355 2.67166 11.0948 2.52733 11.0948 2.37734ZM4.63071 1.13206H7.3697L7.53951 1.8113H4.46087L4.63071 1.13206ZM9.32707 3.05494C9.3916 3.1257 9.4233 3.22135 9.41311 3.31702L8.72933 10.1542C8.69028 10.5601 8.34839 10.8691 7.94085 10.868H4.06015C3.65261 10.8691 3.31072 10.5601 3.27167 10.1542L2.58789 3.3176C2.57827 3.22194 2.6094 3.12684 2.67393 3.05552C2.73846 2.9842 2.82959 2.94345 2.92582 2.94345H9.07529C9.17151 2.94288 9.26255 2.98362 9.32707 3.05494ZM5.09484 4.52832C4.94483 4.52832 4.80051 4.58775 4.69465 4.69416C4.58824 4.80001 4.52881 4.94435 4.52881 5.09434V7.81131C4.52881 8.12375 4.78239 8.37734 5.09484 8.37734C5.40728 8.37734 5.66086 8.12375 5.66086 7.81131V5.09434C5.66086 4.94434 5.60143 4.80001 5.49502 4.69416C5.38917 4.58774 5.24483 4.52832 5.09484 4.52832ZM6.50599 4.69416C6.61185 4.58775 6.75617 4.52832 6.90618 4.52832C7.05617 4.52832 7.20051 4.58774 7.30636 4.69416C7.41277 4.80001 7.4722 4.94434 7.4722 5.09434V7.81131C7.4722 8.12375 7.21862 8.37734 6.90618 8.37734C6.59373 8.37734 6.34015 8.12375 6.34015 7.81131V5.09434C6.34015 4.94435 6.39958 4.80001 6.50599 4.69416Z" fill="#FF4949" />
                                  </svg>
                                </IconButton>
                              </Stack>
                            </Stack>
                          ))}
                        </AccordionDetails>
                      </Accordion>
                    </Stack>
                    <Divider sx={{ borderStyle: 'dashed', borderWidth: '1px', marginTop: '1rem', marginBottom: '1rem' }} />
                  </>))}
                </Stack>
              </Box>

            </Box>
          )}
        </Box>
        <Box className="drawer-footer">
          <Stack direction="row" spacing={2}>
            <Button fullWidth size="large" className="cancel-btn" onClick={onClose}>
              Cancel
            </Button>
            <Button
              fullWidth
              variant="contained"
              size="large"
              disabled={isSubmitting || !isActionable}
              type="submit"
              className="update-btn"
            >
              {isSubmitting ? 'Updating...' : 'Update'}
            </Button>
          </Stack>
        </Box>
      </FormProvider>
    </Box>
  );
};

export default UpdateReleventSkillsDrawer;
